import {
  type ChangeEventHandler,
  type ComponentType,
  type FocusEventHandler,
  useEffect,
  useState,
} from "react";
import { withFormik } from "formik";
import type { FormikErrors, FormikTouched } from "formik";
import { isEmpty } from "lodash-es";
import RpMUIDialog from "@/components/modals/RpMUIModal";
import PaymentBase from "@/components/stripe/Payment";
import { selfSubscribeFormValidation } from "@/helpers/validations";
import { subscriptionService } from "@/features/subscriptions/services/subscriptionService";
import SubscriptionFormBase from "./SubscriptionForm";
import { toastr } from "react-redux-toastr";
import { HTTP_STATUS } from "@/config/constants";
import { Button } from "@mui/material";
import { getDashboardPath } from "@/helpers/utils";

type PlanData = {
  amount: string | number;
  id: string | number;
  period: string | number;
  title: string;
  [key: string]: unknown;
};

type BrandData = {
  brand_name?: string;
  id: string | number;
  system_generated_brand_id?: string | number;
  [key: string]: unknown;
};

type LoggedInUser = {
  email?: string;
  [key: string]: unknown;
};

type LocationOption = {
  label: string;
  value: string | number;
};

type ParticipantLocation = {
  id: string | number;
  location_name?: string;
  system_generated_location_id?: string | number;
};

type SubscriptionValues = {
  amount: string | number;
  email: string;
  locations: LocationOption[];
};

type SubscriptionPlanOuterProps = {
  planData: PlanData;
  brandData: BrandData;
  navigate: (_path: string, _options?: { replace?: boolean }) => void;
  loggedInUser?: LoggedInUser;
};

type SubscriptionPlanProps = SubscriptionPlanOuterProps & {
  values: SubscriptionValues;
  touched: FormikTouched<SubscriptionValues>;
  errors: FormikErrors<SubscriptionValues>;
  handleChange: ChangeEventHandler<HTMLInputElement>;
  handleBlur: FocusEventHandler<HTMLInputElement>;
  setFieldValue: (_field: keyof SubscriptionValues, _value: unknown) => void;
  resetForm: () => void;
  setFieldTouched: (_field: keyof SubscriptionValues) => void;
};

type LocationLookupResponse = {
  status?: number;
  data?: {
    participantLocations?: ParticipantLocation[];
  };
};

type SubscriptionFormProps = {
  values: SubscriptionValues;
  onClick: () => void;
  planData: PlanData;
  brandData: BrandData;
  errors: FormikErrors<SubscriptionValues>;
  touched: FormikTouched<SubscriptionValues>;
  participantLocations: ParticipantLocation[];
  isLoadingLocations: boolean;
  handleBlur: FocusEventHandler<HTMLInputElement>;
  handleChange: ChangeEventHandler<HTMLInputElement>;
  onBlurEmail: () => void;
};

type PaymentProps = {
  success: () => void;
  billingDetails: {
    email: string;
  };
  data: Record<string, unknown>;
  api: typeof subscriptionService.selfSubscribe;
};

const SubscriptionForm =
  SubscriptionFormBase as ComponentType<SubscriptionFormProps>;
const Payment = PaymentBase as ComponentType<PaymentProps>;

const SubscriptionPlan = ({
  planData,
  brandData,
  navigate,
  values,
  touched,
  errors,
  handleChange,
  handleBlur,
  setFieldValue,
  resetForm,
  setFieldTouched,
}: SubscriptionPlanProps) => {
  const [makePayment, setMakePayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [participantLocations, setParticipantLocations] = useState<
    ParticipantLocation[]
  >([]);

  const [loading, setLoading] = useState(false);

  const fetchLocationsForEmail = async () => {
    setParticipantLocations([]);
    setLoading(true);
    try {
      const res = (await subscriptionService.checkAndFetchParticipantLocations({
        brandId: brandData.id,
        email: values.email,
        packageId: planData.id,
      })) as LocationLookupResponse;
      if (res.status === HTTP_STATUS.OK) {
        const locs = res.data?.participantLocations || [];
        setParticipantLocations(locs);
      } else {
        setParticipantLocations([]);
        toastr.error(
          "Error",
          "Email does not exists or participant with this email does not belongs to this brand"
        );
      }
    } catch (err) {
      setParticipantLocations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (
      values.email &&
      values.email.trim() !== "" &&
      values.email.trim().length > 2
    ) {
      fetchLocationsForEmail();
    }
    // Preserve the existing mount-only lookup for the initial Formik email.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnClick = () => {
    const keys = Object.keys(values);
    keys.forEach((k) => {
      setFieldTouched(k as keyof SubscriptionValues);
    });
    if (
      isEmpty(errors) &&
      values.email.trim() !== "" &&
      values.locations.length !== 0
    ) {
      setMakePayment(true);
    }
  };

  const getLocationSystemGeneratedId = (locId: string | number) => {
    const loc = participantLocations.find((l) => l.id === locId);
    if (loc) {
      return loc.system_generated_location_id;
    }
    return "";
  };

  const getAmountToPay = () => {
    return (
      parseInt(String(values.locations.length)) *
      parseFloat(String(planData.amount)) *
      parseInt(String(planData.period))
    );
  };

  const handleOnBlurEmail = () => {
    if (
      !errors?.email &&
      values.email &&
      values.email.trim() !== "" &&
      values.email.trim().length > 2
    ) {
      setFieldValue("locations", []);
      fetchLocationsForEmail();
    }

    if (errors.email) {
      setFieldValue("locations", []);
      setParticipantLocations([]);
    }
  };

  return (
    <>
      <SubscriptionForm
        values={values}
        onClick={handleOnClick}
        planData={planData}
        brandData={brandData}
        errors={errors}
        touched={touched}
        participantLocations={participantLocations}
        isLoadingLocations={loading}
        handleBlur={handleBlur}
        handleChange={handleChange}
        onBlurEmail={handleOnBlurEmail}
      />

      {makePayment && (
        <RpMUIDialog
          open={makePayment}
          onClose={() => setMakePayment(false)}
          heading="Payment"
        >
          <Payment
            success={() => {
              setTimeout(() => {
                setMakePayment(false);
                setPaymentSuccess(true);
                resetForm();
              }, 2000);
            }}
            billingDetails={{
              email: values.email,
            }}
            data={{
              title: planData.title,
              email: values.email,
              amount: getAmountToPay(),
              locations: values.locations.length
                ? values.locations.map((l) => ({
                    id: l.value,
                    name: l.label,
                    system_generated_location_id: getLocationSystemGeneratedId(
                      l.value
                    ),
                  }))
                : [],
              location_ids: values.locations.length
                ? values.locations.map((l) => l.value).join(",")
                : "",
              system_generated_location_ids: values.locations.length
                ? values.locations
                    .map((l) => {
                      const sysId = getLocationSystemGeneratedId(l.value);
                      return sysId ? sysId : l.value;
                    })
                    .join(",")
                : "",
              fk_brand_id: brandData.id,
              system_generated_brand_id: brandData.system_generated_brand_id,
              fk_brand_name: brandData.brand_name,
              fk_package_id: planData.id,
            }}
            api={subscriptionService.selfSubscribe}
          />
        </RpMUIDialog>
      )}

      {paymentSuccess && (
        <RpMUIDialog
          open={paymentSuccess}
          onClose={() => setPaymentSuccess(false)}
          heading="Payment Success"
        >
          <h5 className="text-center mb-3">
            You have successfully subscribed for <br />
            <strong>{planData.title}</strong>
          </h5>
          <h6 className="text-center mb-4">
            Do you have any more locations you would like to subscribe?
          </h6>
          <div className="d-flex-center">
            <Button
              variant="outlined"
              color="primary"
              className="mr-3"
              onClick={() => {
                navigate(`${getDashboardPath()}`, {
                  replace: true,
                });
                // history.goBack();
              }}
            >
              No
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setPaymentSuccess(false)}
            >
              Yes
            </Button>
          </div>
        </RpMUIDialog>
      )}
    </>
  );
};

const SubscriptionPlanForm = withFormik<
  SubscriptionPlanOuterProps,
  SubscriptionValues
>({
  mapPropsToValues: ({ planData, loggedInUser }) => ({
    email: isEmpty(loggedInUser) ? "" : loggedInUser.email,
    locations: [],
    amount: planData.amount,
  }),
  validate: selfSubscribeFormValidation,
  handleSubmit: () => {},
  displayName: "SubscriptionPlanForm",
})(SubscriptionPlan);

export default SubscriptionPlanForm;
